import { respondToHandshake, startHandshake, type InitiatorHandshake, type KeyPair, type SecureChannel } from "@foreman/crypto";
import { decodeMessage, encodeMessage, type Message } from "@foreman/protocol";

/**
 * End-to-end encrypted link over any byte pipe that preserves message boundaries
 * (a WebSocket through the relay). Frame = 1 kind byte || payload:
 *   Hello (phone -> daemon): handshake message 1
 *   Reply (daemon -> phone): handshake message 2
 *   Data  (both ways):       SecureChannel frame carrying one encoded Message
 * The relay forwards these without being able to read or alter them undetected.
 */

const Kind = { Hello: 1, Reply: 2, Data: 3 } as const;

export class LinkError extends Error {
  constructor(
    readonly code: "unauthorized" | "protocol" | "crypto",
    message: string,
  ) {
    super(message);
  }
}

function frame(kind: number, payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(1 + payload.length);
  out[0] = kind;
  out.set(payload, 1);
  return out;
}

function decryptBytes(channel: SecureChannel, body: Uint8Array): Uint8Array {
  try {
    return channel.decrypt(body);
  } catch (e) {
    throw new LinkError("crypto", `bad frame: ${(e as Error).message}`);
  }
}

function decryptMessage(channel: SecureChannel, body: Uint8Array): Message {
  const plaintext = decryptBytes(channel, body);
  try {
    return decodeMessage(plaintext);
  } catch (e) {
    throw new LinkError("protocol", `bad message: ${(e as Error).message}`);
  }
}

/** The `t` of a message we could not understand, for logs and the reply (never anything else from it). */
function messageType(plaintext: Uint8Array): string {
  try {
    const t = (JSON.parse(new TextDecoder().decode(plaintext)) as { t?: unknown }).t;
    return typeof t === "string" ? t.slice(0, 40) : "?";
  } catch {
    return "?";
  }
}

/** Daemon side: one per connected phone. Any thrown LinkError means "drop this peer". */
export class ResponderLink {
  private channel: SecureChannel | null = null;
  peerPublicKey: Uint8Array | null = null;

  constructor(
    private readonly opts: {
      keys: KeyPair;
      /** Device authorization: only paired, non-revoked keys may connect. */
      isKnownPeer: (publicKey: Uint8Array) => boolean;
      sendRaw: (frame: Uint8Array) => void;
      onMessage: (msg: Message, peerPublicKey: Uint8Array) => void;
      /**
       * An authentic message this version can't parse (usually a newer app talking to an older computer). With this set,
       * the link stays up and the owner can answer "unsupported" instead of dropping the phone.
       */
      onUnsupported?: (type: string, peerPublicKey: Uint8Array) => void;
    },
  ) {}

  get ready(): boolean {
    return this.channel !== null;
  }

  receive(data: Uint8Array): void {
    const kind = data[0];
    const body = data.subarray(1);
    if (!this.channel) {
      if (kind !== Kind.Hello) throw new LinkError("protocol", "expected hello");
      let result;
      try {
        result = respondToHandshake(this.opts.keys, body, this.opts.isKnownPeer);
      } catch (e) {
        const message = (e as Error).message;
        throw new LinkError(/unknown or revoked/.test(message) ? "unauthorized" : "protocol", message);
      }
      this.channel = result.channel;
      this.peerPublicKey = result.peerPublicKey;
      this.opts.sendRaw(frame(Kind.Reply, result.message2));
      return;
    }
    if (kind !== Kind.Data) throw new LinkError("protocol", "unexpected frame kind");
    const plaintext = decryptBytes(this.channel, body);
    let msg: Message;
    try {
      msg = decodeMessage(plaintext);
    } catch (e) {
      if (!this.opts.onUnsupported) throw new LinkError("protocol", `bad message: ${(e as Error).message}`);
      return this.opts.onUnsupported(messageType(plaintext), this.peerPublicKey!);
    }
    this.opts.onMessage(msg, this.peerPublicKey!);
  }

  send(msg: Message): void {
    if (!this.channel) throw new Error("link not ready");
    this.opts.sendRaw(frame(Kind.Data, this.channel.encrypt(encodeMessage(msg))));
  }
}

/** Phone side: one per connection attempt. */
export class InitiatorLink {
  private channel: SecureChannel | null = null;
  private handshake: InitiatorHandshake;

  constructor(
    private readonly opts: {
      keys: KeyPair;
      /** The daemon's public key, pinned at pairing. A relay cannot substitute another. */
      daemonPublicKey: Uint8Array;
      sendRaw: (frame: Uint8Array) => void;
      onReady: () => void;
      onMessage: (msg: Message) => void;
    },
  ) {
    this.handshake = startHandshake(opts.keys, opts.daemonPublicKey);
  }

  get ready(): boolean {
    return this.channel !== null;
  }

  start(): void {
    this.opts.sendRaw(frame(Kind.Hello, this.handshake.message1));
  }

  receive(data: Uint8Array): void {
    const kind = data[0];
    const body = data.subarray(1);
    if (!this.channel) {
      if (kind !== Kind.Reply) throw new LinkError("protocol", "expected reply");
      try {
        this.channel = this.handshake.finish(body);
      } catch (e) {
        throw new LinkError("protocol", (e as Error).message);
      }
      this.opts.onReady();
      return;
    }
    if (kind !== Kind.Data) throw new LinkError("protocol", "unexpected frame kind");
    this.opts.onMessage(decryptMessage(this.channel, body));
  }

  send(msg: Message): void {
    if (!this.channel) throw new Error("link not ready");
    this.opts.sendRaw(frame(Kind.Data, this.channel.encrypt(encodeMessage(msg))));
  }
}
